import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { GuestDetailsForm } from '@packages/types';
import { getEmcBoost } from '@packages/utils';

import { Button } from '@elements/button';
import { Text } from '@elements/text';
import { useCheckoutFlow, useEmcTraveller } from '@hooks';
import { i18n } from '@i18n';
import {
  getTripSelector,
  useBoostDetailsStore,
  useCheckoutStore,
  useEmcStore,
  useTripStore,
} from '@store';
import { testProps } from '@utils';

type EmcConfirmAssessmentProps = {
  travellerId?: string;
  age: number;
  firstName: string;
  lastName: string;
};

function useGetConfirmAssessmentType(travellerId?: string) {
  const { getEmcTravellerById } = useEmcTraveller();
  const emcTraveller = getEmcTravellerById(travellerId);
  const nameChangedTravellers = useEmcStore(state => state.nameChangedTravellers);
  const shouldRedeclare = !!emcTraveller?.requireRedeclare && !!emcTraveller.isCompleted;
  const hasTravellerNameChanged = !!nameChangedTravellers?.includes(travellerId ?? '');

  if (shouldRedeclare) {
    return 'DISCOUNT_CODE_APPLIED_OR_REMOVED';
  }

  if (hasTravellerNameChanged) {
    return 'NAME_CHANGED';
  }

  return null;
}

export const EmcConfirmAssessment = ({
  travellerId,
  age,
  firstName,
  lastName,
}: EmcConfirmAssessmentProps) => {
  const setEmcDeclaring = useBoostDetailsStore(state => state.setEmcDeclaring);
  const trip = useTripStore(getTripSelector);
  const emcBoost = getEmcBoost(trip);
  const setOpenEmcSection = useEmcStore(state => state.setOpenEmcSection);
  const { getEmcTravellerById } = useEmcTraveller();
  const emcTraveller = getEmcTravellerById(travellerId);
  const hasConfirmedAssessment = useCheckoutStore(state => state.hasConfirmedAssessment);
  const setOpenSection = useCheckoutStore(state => state.setOpenSection);
  const confirmationType = useGetConfirmAssessmentType(travellerId);
  const { shouldShowPriceModal } = useCheckoutFlow();
  const { watch, trigger } = useFormContext<GuestDetailsForm>();

  // if requireRedeclare is true, always open traveller details sections
  useEffect(() => {
    if (emcTraveller?.requireRedeclare) {
      setOpenSection('travellerDetails');
    }
  }, [emcTraveller?.requireRedeclare, setOpenSection]);

  if (!emcTraveller || !travellerId || !emcBoost || !confirmationType) {
    return null;
  }

  const handleConfirmAssessment = async () => {
    const isValid = await trigger(['firstName', 'lastName'], {
      shouldFocus: true,
    });

    if (!isValid) {
      return;
    }
    const checkoutResult = shouldShowPriceModal(watch());
    if (checkoutResult === 'priceChangeModal') {
      return;
    }

    setEmcDeclaring({
      boostId: emcBoost?.boostId ?? '',
      firstName,
      lastName,
      travellerId: travellerId,
      age,
      screeningData:
        emcTraveller?.decryptedScreeningResult?.[0]?.ScreeningPath?.[0]?.SystemData?.ScreeningData,
      requireReDeclare: true,
    });
    setOpenEmcSection('pre-assessment');
  };
  return (
    <>
      <Button
        variant="feature"
        className={twMerge('mb-4', !hasConfirmedAssessment && 'border-2 border-red-500')}
        onClick={handleConfirmAssessment}
        title={i18n.t('emcShared.global.redeclare.button')}
        {...testProps('btn-confirm')}
      />
      {(!hasConfirmedAssessment || emcTraveller?.requireRedeclare) && (
        <Text className="mb-4 text-red-500" variant="footnote-12/r">
          {i18n.t('emcShared.global.redeclare.error')}
        </Text>
      )}
    </>
  );
};
